import { Box, Chip, Link, Typography } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { RevivnDataGrid } from "components/DataGrid/RevivnDataGrid";
import { LogisticsTicket, LogisticsTicketState } from "types/logisticsTicket";
import { format } from "date-fns";

const STATE_STYLES: Record<LogisticsTicketState, { label: string; backgroundColor: string; color: string }> = {
  new_orders: { label: "NEW ORDERS", backgroundColor: "#E8F1FF", color: "#1D64C8" },
  pending_confirmation: { label: "PENDING CONFIRMATION", backgroundColor: "#FFF1E0", color: "#C77700" },
  scheduled_confirmed: { label: "SCHEDULED CONFIRMED", backgroundColor: "#E8F1FF", color: "#1D64C8" },
  picked_up: { label: "PICKED UP", backgroundColor: "#E8F1FF", color: "#1D64C8" },
  delivery_verified: { label: "DELIVERY VERIFIED", backgroundColor: "#E8F1FF", color: "#1D64C8" },
  canceled: { label: "CANCELED", backgroundColor: "#E8F1FF", color: "#1D64C8" },
};

const LogisticsTicketStatusChip = ({ state }: { state: LogisticsTicketState }) => {
  const style = STATE_STYLES[state];

  return (
    <Chip
      label={style.label}
      size="small"
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        fontWeight: 600,
        fontSize: 10,
        letterSpacing: 0.2,
        height: 24,
        borderRadius: "999px",
        "& .MuiChip-label": { px: 1.25 },
      }}
    />
  );
};

const columns: GridColDef<LogisticsTicket>[] = [
  {
    field: "id",
    headerName: "PICKUP ID",
    width: 140,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Typography variant="body2" color="text.primary">
        <Link href={`/logistics_platform/pickups/${row.uuid}`}>{row.pickup.orderNumber}</Link>
      </Typography>
    ),
  },
  {
    field: "state",
    headerName: "STATUS",
    width: 200,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => <LogisticsTicketStatusChip state={row.state} />,
  },
  {
    field: "client",
    headerName: "CLIENT",
    flex: 1,
    minWidth: 220,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Typography variant="body2" fontWeight={600} fontSize={13}>
        {row.pickup.organization.name}
      </Typography>
    ),
  },
  {
    field: "location",
    headerName: "LOCATION",
    width: 170,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Typography variant="body2" fontSize={13}>
        {[row.pickup.location?.city, row.pickup.location?.state].filter(Boolean).join(", ")}
      </Typography>
    ),
  },
  {
    field: "scheduledAt",
    headerName: "PICKUP DATE",
    width: 120,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Typography variant="body2">{row.scheduledAt ? format(new Date(row.scheduledAt), "MMM d") : ""}</Typography>
    ),
  },
  {
    field: "vendor",
    headerName: "VENDOR",
    width: 220,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
        <Typography variant="body2" noWrap>
          {row.winningQuote?.vendor?.name}
        </Typography>
      </Box>
    ),
  },
  {
    field: "assets",
    headerName: "ASSETS",
    width: 90,
    sortable: false,
    type: "number",
    align: "right",
    headerAlign: "right",
  },
  {
    field: "cost",
    headerName: "COST",
    width: 110,
    sortable: false,
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }: GridRenderCellParams<LogisticsTicket>) => (
      <Typography variant="body2">
        {row.winningQuote?.amountInCents ? `$${(row.winningQuote.amountInCents / 100).toFixed(2)}` : ""}
      </Typography>
    ),
  },
];

export default function LogisticsTicketsTableView({ logisticsTickets }: { logisticsTickets: LogisticsTicket[] }) {
  return (
    <RevivnDataGrid
      rows={logisticsTickets || []}
      columns={columns}
      autoHeight={false}
      disableSelectionOnClick
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      hideFooter
      components={{
        Toolbar: () => null,
      }}
      sx={{
        border: "none",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#fff",
          borderBottom: "1px solid",
          borderColor: "divider",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          color: "text.secondary",
        },
        "& .MuiDataGrid-cell": {
          borderColor: "divider",
          py: 1.25,
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "#F8FAFC",
        },
      }}
    />
  );
}
